import { useState } from 'react';
import Button from '../../../../components/Button/Button';
import FormHeading from '../../../../components/Typography/FormHeading';
import UploadProgressBar from '../../../../components/UploadProgressBar';
import useDocumentUpload from '../../../../hooks/useDocumentUpload';
import {
  DEAL_STATUS_DOCUMENTS_PROCESSING,
  DEAL_STATUS_WAITING_FOR_FREEDOM,
  DEAL_STATUS_WAITING_FOR_REGISTRATION_DOCUMENTS,
  DEAL_STEPS,
  DOCUMENT_TYPES_CERTIFICATE_OF_CIRCUMSTANCES,
  DOCUMENT_TYPES_CONTRACT,
  DOCUMENT_TYPES_GUARANTEE_LETTER,
  DOCUMENT_TYPES_INVOICE,
  DOCUMENT_TYPES_POLES,
  DOCUMENT_TYPES_TECH_PASSPORT,
  PAYMENT_STATUS_PAID,
} from '../../../../constants';
import LoaderInline from '../../../../components/Loader/LoaderInline';
import FreedomClientDocuments from './ClientDocuments';
import FreedomPaymentStatus from './PaymentStatus';
import FreedomCarRegistration from './CarRegistration';
import editDeal from '../../../../api/deals/editDeal';
import generateAllowedDocumentsJson from '../../../../utils/generateAllowedDocumentsJson';
import setStep from '../../../../api/deals/setStep';
import sendMarriedDoc from '../../../../api/creditPlatform/sendMarriedDoc';
import alert from '../../../../helpers/alert';
import initialFeeCreditCardPayment from '../../../../api/deals/initialFeeCreditCardPayment';
import ffinReRegisterConfirm from '../../../../api/bank/ffinReRegisterConfirm';
import ffinAddOwner from '../../../../api/bank/ffinAddOwner';
import ffinRestartOwner from '../../../../api/bank/ffinRestartOwner';
import ffinUpdateOwnerPhone from '../../../../api/bank/ffinUpdateOwnerPhone';
import extractErrorMessage from '../../../../utils/extractErrorMessage';
import StepControlButtons from '../CommonBankSteps/StepControlButtons';
import ContractSign from '../CommonBankSteps/ContractSign';
import FreedomOwnerInfo from './OwnerInfo';

const FreedomFlow = ({
  step,
  deal,
  documents,
  backHandler,
  update,
  chosenBankAppealId,
  chosenBankAppeal,
  cars,
  bankId,
}) => {
  const [loading, setLoading] = useState(false);
  const { upload, uploading, progress, currentFileName } = useDocumentUpload();

  const onPaymentSubmit = (values) => {
    if (values?.status?.value != PAYMENT_STATUS_PAID) return;

    setLoading(true);
    editDeal({
      id: deal?.id,
      statusPaymentId: values?.status?.value,
      statusId: DEAL_STATUS_WAITING_FOR_REGISTRATION_DOCUMENTS,
      ...generateAllowedDocumentsJson([
        DOCUMENT_TYPES_CERTIFICATE_OF_CIRCUMSTANCES,
        DOCUMENT_TYPES_GUARANTEE_LETTER,
        DOCUMENT_TYPES_INVOICE,
      ]),
    })
      .then(async () => {
        await setStep({ dealId: deal?.id, step: DEAL_STEPS.FREEDOM.AUTO_REGISTRATION });

        alert.success('Статус оплаты успешно обновлен');
        update && update();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const sendPaymentSms = () => {
    setLoading(true);
    initialFeeCreditCardPayment({
      bankId: bankId,
      chunkId: deal?.chunkId,
    })
      .then(() => {
        alert.success('Смс со ссылкой на оплату отправлено на номер телефона клиента');
      })
      .catch((err) => {
        alert.error(err?.response?.data?.message || 'Не удалось отправить смс со ссылкой на оплату')
      })
      .finally(() => setLoading(false));
  };


  // Комиссионное авто (THRIFT / THRIFT_SUBSIDIZED / THRIFT_CASCADE): банк
  // валидирует наличие владельца в /re-register/confirm, поэтому до отправки
  // владельца шаг перерегистрации закрываем. ownerSentAt проставляет бэкенд
  // после успешного POST /owner.
  const isConsignment = (chosenBankAppeal?.stockType || '').toUpperCase().startsWith('THRIFT');
  const ownerSent = Boolean(chosenBankAppeal?.ownerSentAt);
  const needsOwner = isConsignment && !ownerSent;

  // Комиссионное авто идёт по укороченному маршруту: ДКП → оплата ПВ →
  // перерегистрация (владелец + регистрация ТС) → выдача. Экраны «ожидание
  // действий клиента на сайте банка» и «документы клиента» относятся только к
  // новым авто, для комиссионки их пропускаем.
  const stepAfterSign = isConsignment
    ? DEAL_STEPS.FREEDOM.WAITING_FOR_PAYMENT
    : DEAL_STEPS.FREEDOM.WAITING_FOR_CLIENT_ACTIONS_ON_FREEDOM_SITE;
  const stepBeforePayment = isConsignment
    ? DEAL_STEPS.FREEDOM.SIGN_CONTRACT
    : DEAL_STEPS.FREEDOM.WAITING_FOR_CLIENT_DOCUMENTS;

  const onOwnerSubmit = async (values) => {
    setLoading(true);
    try {
      await ffinAddOwner({
        appealId: chosenBankAppealId,
        iin: (values?.iin || '').replace(/\D/g, ''),
        mobilePhone: (values?.mobilePhone || '').replace(/\D/g, ''),
      });
      alert.success('Данные владельца отправлены в банк. Владельцу направлено СМС для согласия на продажу');
      update && update();
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || 'Freedom отклонил данные владельца');
    } finally {
      setLoading(false);
    }
  };

  const onOwnerChangePhone = async (values) => {
    setLoading(true);
    try {
      await ffinUpdateOwnerPhone({
        appealId: chosenBankAppealId,
        mobilePhone: (values?.mobilePhone || '').replace(/\D/g, ''),
      });
      alert.success('Номер владельца изменён. СМС для согласия отправлено на новый номер');
      update && update();
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || 'Не удалось изменить номер владельца');
    } finally {
      setLoading(false);
    }
  };

  const onOwnerRestart = async () => {
    setLoading(true);
    try {
      await ffinRestartOwner({ appealId: chosenBankAppealId });
      alert.success('Проверка владельца перезапущена');
      update && update();
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || 'Не удалось перезапустить проверку владельца');
    } finally {
      setLoading(false);
    }
  };

  const onRegistrationSubmit = async (values) => {
    if (!values?.status) {
      alert.error('Необходимо выбрать статус сделки');
      return;
    }
    if (needsOwner && values?.status?.value === DEAL_STATUS_WAITING_FOR_FREEDOM) {
      alert.error('Сначала отправьте данные владельца — банк не подтвердит перерегистрацию комиссионного авто без них');
      return;
    }
    try {
      await editDeal({
        id: deal?.id,
        statusId: values?.status?.value,
        ...generateAllowedDocumentsJson([
          DOCUMENT_TYPES_CERTIFICATE_OF_CIRCUMSTANCES,
          DOCUMENT_TYPES_GUARANTEE_LETTER,
          DOCUMENT_TYPES_INVOICE,
          DOCUMENT_TYPES_TECH_PASSPORT,
          DOCUMENT_TYPES_CONTRACT,
          DOCUMENT_TYPES_POLES,
        ]),
      });
      if (values?.status?.value === DEAL_STATUS_WAITING_FOR_FREEDOM) {
        await ffinReRegisterConfirm({ appealId: chosenBankAppealId });
        await setStep({ dealId: deal?.id, step: DEAL_STEPS.FREEDOM.WAITING_FOR_FINAL_ACTIONS });
      }
      update && update();
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || 'Ошибка при подтверждении перерегистрации');
    }
  };

  const uploadClientDocuments = async (values) => {
    try {
      await upload({
        dealId: deal?.id,
        fileTypeId: DOCUMENT_TYPES_CERTIFICATE_OF_CIRCUMSTANCES,
        file: values['doc'],
      });
      await sendMarriedDoc({ appealId: chosenBankAppealId });
      await editDeal({
        id: deal?.id,
        statusId: DEAL_STATUS_WAITING_FOR_REGISTRATION_DOCUMENTS,
        ...generateAllowedDocumentsJson([
          DOCUMENT_TYPES_CERTIFICATE_OF_CIRCUMSTANCES,
          DOCUMENT_TYPES_GUARANTEE_LETTER,
          DOCUMENT_TYPES_INVOICE,
        ]),
      });
      await setStep({ dealId: deal?.id, step: DEAL_STEPS.FREEDOM.WAITING_FOR_PAYMENT });
      update();
    } catch (err) {
      alert.error('Произошла ошибка при отправке документов в банк');
    }
  };

  const goBack = (statusId, step) => {
    setLoading(true);
    editDeal({
      id: deal?.id,
      statusId,
      step,
    })
      .then(() => {
        update && update();
      })
      .finally(() => setLoading(false));
  };

  if (uploading) {
    return <UploadProgressBar progress={progress} fileName={currentFileName} />;
  }

  if (loading) {
    return <LoaderInline />;
  }

  if (step === DEAL_STEPS.FREEDOM.SIGN_CONTRACT) {
    return <ContractSign deal={deal} documents={documents} cars={cars} dealStepAfterSign={stepAfterSign} setLoading={setLoading} update={update}/>;
  }

  if (step === DEAL_STEPS.FREEDOM.WAITING_FOR_CLIENT_ACTIONS_ON_FREEDOM_SITE) {
    return (
      <>
        <FormHeading>Статус</FormHeading>
        <Button
          text={'Ожидаем завершения действий клиента на сайте Freedom Bank'}
          style={{ color: 'rgb(73, 65, 54)', border: '1px solid rgb(73, 65, 54)' }}
        />
        <div style={{ marginTop: 10 }}>
          <Button
            text={'Назад'}
            onClick={() => goBack(DEAL_STATUS_DOCUMENTS_PROCESSING, 5)}
            style={{ color: '#BABABA', border: '1px solid #BABABA', width: '140px' }}
          />
        </div>
        {/* <Button
          onClick={() => {
            setStep({ dealId: deal?.id, step: DEAL_STEPS.FREEDOM.WAITING_FOR_CLIENT_DOCUMENTS }).then(() => {
              update();
            });
          }}
          fill
        >
          Далее
        </Button> */}
      </>
    );
  }
  if (step === DEAL_STEPS.FREEDOM.WAITING_FOR_CLIENT_DOCUMENTS) {
    return (
      <>
        <StepControlButtons
          dealId={deal?.id}
          nextStep={DEAL_STEPS.FREEDOM.WAITING_FOR_PAYMENT}
          update={update}
          setLoading={setLoading}
        />
        <FreedomClientDocuments documents={documents} onSubmit={uploadClientDocuments} loading={uploading} />
        <div style={{ marginTop: 10 }}>
          <Button
            text={'Назад'}
            onClick={() => goBack(deal?.status?.id, DEAL_STEPS.FREEDOM.WAITING_FOR_CLIENT_ACTIONS_ON_FREEDOM_SITE)}
            style={{ color: '#BABABA', border: '1px solid #BABABA', width: '140px' }}
          />
        </div>
      </>
    );
  }
  if (step === DEAL_STEPS.FREEDOM.WAITING_FOR_PAYMENT) {
    return (
      <>
        <StepControlButtons
          dealId={deal?.id}
          prevStep={stepBeforePayment}
          nextStep={DEAL_STEPS.FREEDOM.AUTO_REGISTRATION}
          update={update}
          setLoading={setLoading}
        />
        <FreedomPaymentStatus onSubmit={onPaymentSubmit} documents={documents} sendPaymentSms={sendPaymentSms}/>
        <div style={{ marginTop: 10 }}>
          <Button
            text={'Назад'}
            onClick={() => goBack(deal?.status?.id, stepBeforePayment)}
            style={{ color: '#BABABA', border: '1px solid #BABABA', width: '140px' }}
          />
        </div>
      </>
    );
  }
  if (step === DEAL_STEPS.FREEDOM.AUTO_REGISTRATION) {
    return (
      <>
        <StepControlButtons
          dealId={deal?.id}
          prevStep={DEAL_STEPS.FREEDOM.WAITING_FOR_PAYMENT}
          update={update}
          setLoading={setLoading}
        />
        {isConsignment && (
          <FreedomOwnerInfo
            onSubmit={onOwnerSubmit}
            onChangePhone={onOwnerChangePhone}
            onRestart={onOwnerRestart}
            loading={loading}
            sent={ownerSent}
            revisionReason={chosenBankAppeal?.errorMessage}
          />
        )}
        <FreedomCarRegistration onSubmit={onRegistrationSubmit} loading={uploading} />
        <div style={{ marginTop: 10 }}>
          <Button
            text={'Назад'}
            onClick={() => goBack(deal?.status?.id, DEAL_STEPS.FREEDOM.WAITING_FOR_PAYMENT)}
            style={{ color: '#BABABA', border: '1px solid #BABABA', width: '140px' }}
          />
        </div>
      </>
    );
  }

  if (step === DEAL_STEPS.FREEDOM.WAITING_FOR_FINAL_ACTIONS) {
    return (
      <>
        <FormHeading>Статус</FormHeading>
        <Button
          text={'Ожидаем завершения на стороне Freedom Bank'}
          style={{ color: 'rgb(73, 65, 54)', border: '1px solid rgb(73, 65, 54)' }}
        />
        <div style={{ marginTop: 10 }}>
          <Button
            text={'Назад'}
            onClick={() => goBack(deal?.status?.id, DEAL_STEPS.FREEDOM.AUTO_REGISTRATION)}
            style={{ color: '#BABABA', border: '1px solid #BABABA', width: '140px' }}
          />
        </div>
      </>
    );
  }
};

export default FreedomFlow;
